import React, { useRef, useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import TabTitle from "./TabTitle";
import ButtonGrid from "./ButtonGrid";
import styles from "./MenuLayoutEdit.module.css";
import AddMenuItemDialog from "./AddMenuItemDialog";
import { post, get, put } from "../../../Common/api";
import Alert, {
  success as successNotificationType,
  error as errorNotificationType,
  getNotificationForError,
} from "../../Alert";
import { defaultErrorMessage } from "../../../Common/UserException";
import {
  setCurrentPageNotification,
  setNextPageNotification,
  selectNotification,
} from "../../../redux/notificationSlice";
import CancelMenuLayoutDialog from "./CancelMenuLayoutDialog";
import {DropDownList} from "@progress/kendo-react-dropdowns";
import Select from "react-select";
import { select } from "react-select-event";

export default function MenuLayoutEdit() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const layoutType30Button = 1;
  const titleInputRef = useRef(null);
  const pageInputRef = useRef(null);
  const isNewLayout = !id;
  const [selected, setSelected] = useState(0);
  const [isSaveEnabled, setIsSaveEnabled] = useState(true);
  const [hasTitleValidationError, setHasTitleValidationError] = useState(false);
  const [isMenuLayoutCancelDialogVisible, setIsMenuLayoutCancelDialogVisible] =
    React.useState(false);
  const [menuLayoutTypes, setMenuLayoutTypes] = useState([]);
  const blankButtons = [{"columnSpan":1,"rowSpan":1,"columnIndex":0,"rowIndex":0},{"columnSpan":1,"rowSpan":1,"columnIndex":1,"rowIndex":0},{"columnSpan":1,"rowSpan":1,"columnIndex":2,"rowIndex":0},{"columnSpan":1,"rowSpan":1,"columnIndex":3,"rowIndex":0},{"columnSpan":1,"rowSpan":1,"columnIndex":4,"rowIndex":0},{"columnSpan":1,"rowSpan":1,"columnIndex":0,"rowIndex":1},{"columnSpan":1,"rowSpan":1,"columnIndex":1,"rowIndex":1},{"columnSpan":1,"rowSpan":1,"columnIndex":2,"rowIndex":1},{"columnSpan":1,"rowSpan":1,"columnIndex":3,"rowIndex":1},{"columnSpan":1,"rowSpan":1,"columnIndex":4,"rowIndex":1},{"columnSpan":1,"rowSpan":1,"columnIndex":0,"rowIndex":2},{"columnSpan":1,"rowSpan":1,"columnIndex":1,"rowIndex":2},{"columnSpan":1,"rowSpan":1,"columnIndex":2,"rowIndex":2},{"columnSpan":1,"rowSpan":1,"columnIndex":3,"rowIndex":2},{"columnSpan":1,"rowSpan":1,"columnIndex":4,"rowIndex":2},{"columnSpan":1,"rowSpan":1,"columnIndex":0,"rowIndex":3},{"columnSpan":1,"rowSpan":1,"columnIndex":1,"rowIndex":3},{"columnSpan":1,"rowSpan":1,"columnIndex":2,"rowIndex":3},{"columnSpan":1,"rowSpan":1,"columnIndex":3,"rowIndex":3},{"columnSpan":1,"rowSpan":1,"columnIndex":4,"rowIndex":3},{"columnSpan":1,"rowSpan":1,"columnIndex":0,"rowIndex":4},{"columnSpan":1,"rowSpan":1,"columnIndex":1,"rowIndex":4},{"columnSpan":1,"rowSpan":1,"columnIndex":2,"rowIndex":4},{"columnSpan":1,"rowSpan":1,"columnIndex":3,"rowIndex":4},{"columnSpan":1,"rowSpan":1,"columnIndex":4,"rowIndex":4},{"columnSpan":1,"rowSpan":1,"columnIndex":0,"rowIndex":5},{"columnSpan":1,"rowSpan":1,"columnIndex":1,"rowIndex":5},{"columnSpan":1,"rowSpan":1,"columnIndex":2,"rowIndex":5},{"columnSpan":1,"rowSpan":1,"columnIndex":3,"rowIndex":5},{"columnSpan":1,"rowSpan":1,"columnIndex":4,"rowIndex":5}];

  const getLayoutFromDto = (dto, menuLayoutTypesList) => {
    const getButtons = (buttons) => {
      const result = buttons.map((b) => {
        return {
          ...b.menuItem,
          ...b
        };
      });
      return result;
    };
    const layoutResult = {
      title: dto.title,
      nextTab: dto.pages.length + 1,
      tabs: dto.pages.map((t) => {
        return {
          name: t.name,
          menuLayoutTypeId: t.menuLayoutTypeId,
          type: menuLayoutTypesList.filter((mlt) => mlt.value === t.menuLayoutTypeId)[0] || {},
          buttons: getButtons(t.buttons),
        }
      }),
    };
    return layoutResult;
  };

  const getMenuLayoutTypes = (menuLayoutTypesData) => {
    const menuLayoutTypesList = menuLayoutTypesData.map((mlt) => {
      return {
        value: mlt.id,
        label: mlt.description,
        template: JSON.parse(mlt.template || '{}')
      };
    });
    return menuLayoutTypesList;
  };

  useEffect(() => {
    if (id) {
      Promise.all([
        get(`/MenuLayouts/${id}`).catch((error) => {
          dispatch(setNextPageNotification(getNotificationForError(error)));
          redirectToMenuLayoutList();
        }),
        get('/MenuLayoutTypes').catch((error) => {
          dispatch(setCurrentPageNotification(getNotificationForError(error)));
        })
      ]).then(values => {
        const savedLayout = values[0];
        if(savedLayout) {
          const menuLayoutTypesData = values[1];
          const menuLayoutTypesList = getMenuLayoutTypes(menuLayoutTypesData);
          setMenuLayoutTypes(menuLayoutTypesList);
          const currentLayout = getLayoutFromDto(savedLayout, menuLayoutTypesList);        
          setInitialLayout(currentLayout);
          setLayout(currentLayout);
        }
      });
    }
    else {
      get('/MenuLayoutTypes').then(menuLayoutTypesData => {
        const menuLayoutTypesList = getMenuLayoutTypes(menuLayoutTypesData);
        setMenuLayoutTypes(menuLayoutTypesList);
        setInitialLayout(layout);
        setLayout(layout);
      }).catch((error) => {
        dispatch(setCurrentPageNotification(getNotificationForError(error)));
      });
    }
  }, [id, dispatch]);

  const [initialLayout, setInitialLayout] = React.useState({
    title: "Untitled",
    nextTab: 2,
    tabs: [{ name: "Page1", menuLayoutTypeId: 1, type: {value: 1, label: '30 items', template: blankButtons}, buttons: [...blankButtons] }],
  });
  const [layout, setLayout] = React.useState(initialLayout);
  const [isMenuItemDialogVisible, setIsMenuItemDialogVisible] = useState(false);
  const [buttonBeingEdited, setButtonBeingEdited] = useState({});
  const [focusedTabIndex, setFocusedTabIndex] = useState(-1);
  const openDialog = (tabIndex, buttonIndex) => {
    setIsMenuItemDialogVisible(true);
    setButtonBeingEdited({ tabIndex, buttonIndex });
  };
  const cancelDialog = () => {
    setIsMenuItemDialogVisible(false);
  };
  const replaceButton = (tabIndex, buttonIndex, buttonValue) => {
    const makeNewTab = (oldTab) => {
      let newButtons = oldTab.buttons;
      const oldButton = oldTab.buttons[buttonIndex];
      const newButton = {
        ...buttonValue,
        menuItemId: buttonValue.menuItemId || buttonValue.id,
        rowSpan: oldButton.rowSpan,
        columnSpan: oldButton.columnSpan,
        rowIndex: oldButton.rowIndex,
        columnIndex: oldButton.columnIndex,
      };

      newButtons[buttonIndex] = newButton;
      return {
        name: oldTab.name,
        buttons: newButtons,
        menuLayoutTypeId: oldTab.menuLayoutTypeId
      };
    };

    const newLayout = {
      ...layout,
      tabs: layout.tabs.map((t, i) => (tabIndex === i ? makeNewTab(t) : t)),
    };
    setLayout(newLayout);
  };
  const deleteButton = (tabIndex, buttonIndex) => {
    replaceButton(tabIndex, buttonIndex, {});
  };
  const addItem = (item) => {
    setIsMenuItemDialogVisible(false);
    const { tabIndex, buttonIndex } = buttonBeingEdited;
    replaceButton(tabIndex, buttonIndex, item);
  };

  const setTitle = (title) => {
    const truncatedTitle = title.length > 100 ? title.substring(0, 100) : title;
    const newLayout = { ...layout, title: truncatedTitle };
    setLayout(newLayout);
  };

  const setTabName = (name, tabIndex) => {
    const newLayout = {
      ...layout,
      tabs: layout.tabs.map((tab, i) =>
        i === tabIndex ? { ...tab, name } : tab
      ),
    };
    setLayout(newLayout);
  };
  const addTab = (e) => {
    e.stopPropagation();
    const newLayout = {
      ...layout,
      nextTab: layout.nextTab + 1,
      tabs: [
        ...layout.tabs,
        { name: `Page${layout.nextTab}`, menuLayoutTypeId: 1, buttons: [...blankButtons] },
      ],
    };
    setLayout(newLayout);
    const newSelected = newLayout.tabs.length - 1;
    setSelected(newSelected);
    setFocusedTabIndex(newSelected);
  };
  const removeTab = (index) => {
    const newLayout = {
      ...layout,
      tabs: layout.tabs.filter((t, i) => i !== index),
    };
    setLayout(newLayout);
  };

  const handleSelect = (e) => {
    setSelected(e.selected);
  };

  const doSaveValidation = () => {
    let isValid = true;
    if (layout.title.length === 0) {
      window.scrollTo(0, 0);
      titleInputRef.current.focus();
      dispatch(
        setCurrentPageNotification({
          message: "You must specify a menu layout name.",
          type: errorNotificationType,
        })
      );
      setHasTitleValidationError(true);
      isValid = false;
    }
    return isValid;
  };

  const saveLayout = async () => {
    let valid = doSaveValidation();
    if (!valid) {
      return;
    }
    const getButtons = (buttons) => {
      const result = buttons.map((b)=> {
        return {
          columnSpan: b.columnSpan,
          rowSpan: b.rowSpan,
          columnIndex: b.columnIndex,
          rowIndex: b.rowIndex,
          menuItemId: b.menuItemId,
        };
      });
      return result;
    };

    const layoutDto = {
      title: layout.title,
      pages: layout.tabs.map((t) => ({
        name: t.name,
        menuLayoutTypeId: t.menuLayoutTypeId,
        buttons: getButtons(t.buttons),
      })),
    };
    try {
      setIsSaveEnabled(false);
      if (isNewLayout) {
        await post("/MenuLayouts", layoutDto);
      } else {
        await put(`/MenuLayouts/${id}`, layoutDto);
      }
      dispatch(
        setNextPageNotification({
          type: successNotificationType,
          message: `Menu layout "${layout.title}" saved.`,
        })
      );

      history.push("/menulayouts");
    } catch (error) {
      let message = defaultErrorMessage;
      if (error.type === "UserException") {
        message = error.message;
        setHasTitleValidationError(true);
      }
      if (error.type == "NotFoundException") {
        message = error.message;
      }
      dispatch(
        setCurrentPageNotification({
          type: errorNotificationType,
          message: message,
        })
      );
      window.scrollTo(0, 0);
      titleInputRef.current.focus();
    }
    setIsSaveEnabled(true);
  };

  const cancelLayoutChanges = () => {
    if (initialLayout !== layout) {
      setIsMenuLayoutCancelDialogVisible(true);
      return;
    }
    redirectToMenuLayoutList();
  };

  const redirectToMenuLayoutList = () => {
    setIsMenuLayoutCancelDialogVisible(false);
    history.push("/menulayouts");
  };

  const onDrop = (ev, tabIndex, targetButtonIndex, targetButton) => {
    ev.preventDefault();
    const currentTab = layout.tabs[tabIndex];
    const sourceButtonIndex = ev.dataTransfer.getData("buttonIndex");
    let sourceButton = currentTab.buttons[sourceButtonIndex];

    if(sourceButton.menuItemId) {
      replaceButton(tabIndex, targetButtonIndex, sourceButton);
      if(targetButton.menuItemId) {
        replaceButton(tabIndex, sourceButtonIndex, targetButton);
      }
      else {
        deleteButton(tabIndex,sourceButtonIndex);
      }
    }
  };

  const getMergedButtons = (buttons, templateButtons) => {
    const assignedButtons = buttons.filter((b) => b.menuItemId);
    var result = templateButtons.map((tb, i) => {
      let button = assignedButtons[i] || {};
      return {
        ...button,
        rowIndex: tb.rowIndex,
        columnIndex: tb.columnIndex,
        rowSpan: tb.rowSpan,
        columnSpan: tb.columnSpan,
      };
    });
    return result;
  };

  const onDropDownListChange = (selectedOption) => {
    var mergedButtons = getMergedButtons(layout.tabs[selected].buttons || [], selectedOption.template.buttons);
    layout.tabs[selected].buttons = mergedButtons;
    layout.tabs[selected].menuLayoutTypeId = selectedOption.value;
    layout.tabs[selected].type = selectedOption;
    const newLayout = {
      ...layout
    };
    setLayout(newLayout);
  };  
  return (
    <React.Fragment>
      {isMenuItemDialogVisible && (
        <AddMenuItemDialog onCancel={cancelDialog} onAdd={addItem} />
      )}
      <Alert />
      {isMenuLayoutCancelDialogVisible && (
        <CancelMenuLayoutDialog
          onCancel={() => setIsMenuLayoutCancelDialogVisible(false)}
          onOk={redirectToMenuLayoutList}
        />
      )}

      {hasTitleValidationError && (
        <span className={styles.validationMessage}>&gt;</span>
      )}
      <input
        aria-label="menu layout title"
        placeholder="Title"
        ref={titleInputRef}
        placehoder="Title"
        className={`${styles.textBoxTitle} ${
          hasTitleValidationError && styles.validationError
        }`}
        value={layout.title}
        onChange={(event) => setTitle(event.target.value)}
      />

      {layout.tabs.length === 0 ? (
        <TabStrip>
          <TabStripTab
            title={
              <React.Fragment>
                <h2>
                  <span
                    role="button"
                    aria-label="Add"
                    className="k-icon k-i-plus"
                    onClick={addTab}
                  />
                </h2>
              </React.Fragment>
            }
          ></TabStripTab>
        </TabStrip>
      ) : undefined}
      <TabStrip
        selected={
          selected > layout.tabs.length - 1 ? layout.tabs.length - 1 : selected
        }
        onSelect={handleSelect}
      >
        {layout.tabs.map((tab, tabIndex) => (
          <TabStripTab
            key={tabIndex}
            title={
              <TabTitle
                name={tab.name}
                index={tabIndex}
                count={layout.tabs.length}
                addTab={addTab}
                removeTab={() => removeTab(tabIndex)}
                setTabName={(value) => setTabName(value, tabIndex)}
                isFocus={tabIndex === focusedTabIndex}
              />
            }
          >
            <div>
              <div style={{width:'300px'}}>

                <label htmlFor={'MenuLayoutTypes'}>Menu Layout Type:</label>
                  <Select
                    defaultValue={tab && tab.type || (menuLayoutTypes[1] || menuLayoutTypes[0])}
                    value={tab && tab.type}
                    onChange={onDropDownListChange}
                    name={'MenuLayoutTypes'}
                    inputId={'MenuLayoutTypes'}
                    options={menuLayoutTypes}
                    isMulti={false}
                    closeMenuOnSelect={true}
                  />
             
              </div>

              <ButtonGrid
                buttons={tab.buttons}
                onEdit={(buttonIndex) => openDialog(tabIndex, buttonIndex)}
                onDelete={(buttonIndex) => deleteButton(tabIndex, buttonIndex)}
                onDrop={(ev, targetButtonIndex, targetButton) =>
                  onDrop(ev, tabIndex, targetButtonIndex, targetButton)
                }
              />
            </div>
          </TabStripTab>
        ))}
      </TabStrip>
      <div className="col-md-12 section-spacing">
        <div className={`button-wrap-alignment ${styles.buttonContainer}`}>
          <button
            aria-label="Save layout"
            className={`btn button-width ${styles.success} col-md-2 col-md-offset-7`}
            onClick={() => {
              saveLayout();
            }}
            disabled={!isSaveEnabled}
          >
            Save
          </button>
          <button
            aria-label="cancel menu layout changes"
            className="btn button-width col-md-2"
            onClick={() => cancelLayoutChanges()}
          >
            Cancel
          </button>
        </div>
      </div>
    </React.Fragment>
  );
}
